import { roundAndFormat, truncate } from '~/helper/number';
import { AppLink } from '~/components/AppLink';
import { useTranslate } from '@tolgee/react';
import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { clsx } from 'clsx';
import { Button } from '~/components/ui/Button';
import { EmptyState } from '~/components/empty/EmptyState';

interface Props {
  readonly evaluationValue: number;
  readonly differentEvaluationValueInPercent: number;
  readonly evaluationType: string;
  readonly link: string;
  readonly isAuthenticated?: boolean;
  readonly showDemoWidgetContent?: boolean;
}

export function EvaluationWidget(props: Props) {
  const { t } = useTranslate();
  if (
    !props.showDemoWidgetContent &&
    (props.evaluationValue === null || props.evaluationValue === undefined)
  ) {
    return (
      <Card className="flex h-full w-full flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold">
              {t('nav_evaluation')}
            </CardTitle>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <svg
                className="h-4 w-4 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col pt-0">
          <EmptyState
            title={t('nav_evaluation')}
            description={t('empty_no_data_available')}
          />
        </CardContent>
      </Card>
    );
  }

  const demoEvaluationValue = 1200000;
  const evaluationValue = props.showDemoWidgetContent
    ? demoEvaluationValue
    : props.evaluationValue;
  const evaluationType = props.evaluationType || 'short';

  return (
    <Card className="flex h-full w-full flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t('nav_evaluation')}
          </CardTitle>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <svg
              className="h-4 w-4 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </div>
        </div>
      </CardHeader>
      <CardContent
        className={clsx('flex-1 flex flex-col pt-0', {
          'opacity-50': props.showDemoWidgetContent,
        })}
      >
        <div className="h-full flex flex-col gap-y-6">
          {/* Evaluation Type */}
          {evaluationType && (
            <div className="text-sm text-muted-foreground font-medium">
              {t(`evaluation_type_${evaluationType}`)}
            </div>
          )}

          {/* Main Value Display */}
          <div className="flex-1 flex flex-col justify-center">
            {evaluationValue !== null && evaluationValue !== undefined ? (
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-3">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                    CHF {roundAndFormat(evaluationValue)}
                  </div>
                  {(props.differentEvaluationValueInPercent ?? null) !== null &&
                    props.differentEvaluationValueInPercent !== 0 && (
                      <EvaluationDifference
                        differentEvaluationValueInPercent={
                          props.differentEvaluationValueInPercent
                        }
                      />
                    )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Action Button */}
          {!props.showDemoWidgetContent && (
            <div className="flex justify-start">
              <AppLink
                to={`${props.link}#evaluationDetailedEvaluation` as any}
                data-track="immodossier valiant evaluation detail link"
              >
                <Button
                  primary
                  showArrow
                >
                  {t('widget_evaluation_button_text')}
                </Button>
              </AppLink>
            </div>
          )}
        </div>{' '}
      </CardContent>
    </Card>
  );
}

function EvaluationDifference(
  props: Pick<Props, 'differentEvaluationValueInPercent'>,
) {
  const { t } = useTranslate();
  const difference = props.differentEvaluationValueInPercent;

  // Allow rendering even if difference is 0 or null - removed null check to fix empty widget issue

  const isZero = difference === 0;
  const isPositive = difference > 0;
  const formattedChange = isZero
    ? ''
    : `${isPositive ? '+' : ''}${truncate(difference, 2)}%`;

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all',
        {
          'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300':
            isZero,
          'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800':
            isPositive,
          'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800':
            !isZero && !isPositive,
        },
      )}
    >
      {!isZero && (
        <svg
          className="w-3 h-3 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={
              isPositive
                ? 'M5 10l7-7m0 0l7 7m-7-7v18'
                : 'M19 14l-7 7m0 0l-7-7m7 7V3'
            }
          />
        </svg>
      )}
      <span>{formattedChange}</span>
      {isZero && (
        <span className="ml-1 text-[10px] opacity-70">
          ({t('evaluation_detailed_evaluation_widget_unchanged')})
        </span>
      )}
    </span>
  );
}
